import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, HeartCrack } from "lucide-react";
import Logo from "./Logo";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/create", label: "Create Certificate" },
    { path: "/mark-as-ended", label: "Mark as Ended" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="sticky top-0 z-50 bg-[#FFFDF7]/90 backdrop-blur-md border-b border-[#E2E2E2]/50"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-300 relative whitespace-nowrap ${
                  isActive(link.path)
                    ? "text-[#E8A0BF]"
                    : "text-[#5A5A5A] hover:text-[#E8A0BF]"
                } ${link.path === '/mark-as-ended' ? 'flex items-center gap-1' : ''}`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.path === '/mark-as-ended' && <HeartCrack className="w-4 h-4" />}
                {link.label}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#E8A0BF] rounded-full"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden lg:block">
            <Link to="/create">
              <Button 
                className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-5 xl:px-6 py-2 shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                data-testid="nav-create-btn"
              >
                Create Yours
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#2D2D2D]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-2 border-t border-[#E2E2E2]/50">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? "bg-[#E8A0BF]/10 text-[#E8A0BF]"
                        : "text-[#5A5A5A] hover:bg-[#F4F1EA]"
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.path === '/mark-as-ended' && <HeartCrack className="w-4 h-4" />}
                    {link.label}
                  </Link>
                ))}
                <div className="px-4 pt-2">
                  <Link to="/create" onClick={() => setIsOpen(false)}>
                    <Button 
                      className="w-full bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full"
                      data-testid="mobile-create-btn"
                    >
                      Create Your Certificate
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
